<template>
  <el-dialog
    title="员工任职记录与企业微信"
    :visible.sync="isShow"
    width="680px"
    top="6vh"
  >
    <div v-loading="isLoading">
      <div class="personnel-overview">
        <div class="personnel-overview-item">
          <span class="label">员工</span>
          <span>{{ basic.nickname || '-' }}</span>
        </div>
        <div class="personnel-overview-item">
          <span class="label">账号</span>
          <span>{{ basic.username || '-' }}</span>
        </div>
        <div class="personnel-overview-item">
          <span class="label">手机号</span>
          <span>{{ basic.phone || '-' }}</span>
        </div>
        <div class="personnel-overview-item">
          <span class="label">企业微信号</span>
          <span>{{ basic.we_chat || '-' }}</span>
        </div>
        <div class="personnel-overview-item">
          <span class="label">账号状态</span>
          <el-tag :type="basic.is_del ? 'info' : 'success'">{{ basic.is_del ? '已离职/已停用' : '在职' }}</el-tag>
        </div>
      </div>

      <el-card shadow="never" class="wechat-card">
        <div slot="header" class="wechat-card-header">
          <span>企业微信通讯录</span>
          <el-button type="text" size="mini" :loading="wechatLoading" @click="loadWeChatStatus">刷新状态</el-button>
        </div>
        <div v-if="wechatStatus">
          <el-tag :type="wechatTagType">{{ wechatStatusText }}</el-tag>
          <div v-if="wechatStatus.we_chat" class="wechat-id">UserID：{{ wechatStatus.we_chat }}</div>
          <div v-if="wechatStatus.exists === null && wechatStatus.message" class="wechat-error">{{ wechatStatus.message }}</div>
        </div>
        <div v-else class="wechat-status-empty">尚未查询</div>

        <div v-if="canRetryWechat" class="wechat-retry">
          <el-button
            v-if="canBindWechat"
            type="primary"
            size="small"
            :loading="isBinding"
            @click="handleBindWechat"
          >补建企业微信成员并邀请</el-button>
          <el-button
            v-if="canResendInvite"
            type="warning"
            size="small"
            :loading="isInviting"
            @click="handleResendInvite"
          >重新发送加入邀请</el-button>
          <p class="wechat-remove-tip">{{ retryTip }}</p>
        </div>

        <div v-if="canRemoveWechat" class="wechat-remove">
          <el-button type="danger" size="small" :loading="isRemoving" @click="handleRemoveWechat">移出企业微信通讯录</el-button>
          <p class="wechat-remove-tip">仅超级管理员可手动移出；仅当员工已离职/停用且无在职任职记录时可操作。</p>
        </div>
      </el-card>

      <div class="section-title">任职记录</div>
      <div v-if="employmentRecords.length" class="employment-list">
        <div v-for="record in employmentRecords" :key="record.id" class="employment-item">
          <div class="employment-item-header">
            <span class="employment-store">{{ toStoreNameText(record.store_name) }}</span>
            <el-tag v-if="record.is_current" type="success" size="mini">在职</el-tag>
            <el-tag v-else type="info" size="mini">{{ leaveTypeName(record.leave_type) || '已离职' }}</el-tag>
          </div>
          <div class="employment-item-body">
            <div class="employment-item-row">
              <span class="employment-item-label">职位</span>
              <span>{{ record.role_name || '-' }}</span>
            </div>
            <div class="employment-item-row">
              <span class="employment-item-label">入职时间</span>
              <span>{{ toTimeText(record.start_time) }}</span>
            </div>
            <div class="employment-item-row">
              <span class="employment-item-label">离职时间</span>
              <span>{{ record.end_time ? toTimeText(record.end_time) : '在职' }}</span>
            </div>
            <div class="employment-item-row">
              <span class="employment-item-label">在职时长</span>
              <span>{{ toDurationText(record.duration) }}</span>
            </div>
            <div class="employment-item-row">
              <span class="employment-item-label">离职类型</span>
              <span>{{ record.is_current ? '在职' : (leaveTypeName(record.leave_type) || '-') }}</span>
            </div>
            <div class="employment-item-row">
              <span class="employment-item-label">原因/备注</span>
              <span>{{ record.leave_reason || (record.rehire_reason ? '重新入职：' + record.rehire_reason : '-') }}</span>
            </div>
          </div>
        </div>
      </div>
      <div v-else class="employment-empty">暂无任职记录</div>

      <div class="personnel-footer">
        <el-button @click="close">关闭</el-button>
      </div>
    </div>
  </el-dialog>
</template>

<script>
import { mapGetters } from 'vuex';
import {
  getEmploymentRecords,
  getWeChatStatus,
  removeWeChat,
  resendWeChatInvite,
  retryWeChatBind,
} from '@/api/administrator';
import { leaveTypes } from '@/enums/leaveTypes';
import {
  timeToYmdHi,
  formatDuration,
  formatStoreName,
} from '@/filters';

export default {
  name: 'VDialogPersonnel',
  props: {
    value: Boolean,
    administratorId: {
      type: Number,
      default: 0,
    },
  },
  data: () => ({
    isShow: false,
    isLoading: false,
    wechatLoading: false,
    isRemoving: false,
    isBinding: false,
    isInviting: false,
    basic: {},
    employmentRecords: [],
    wechatStatus: null,
  }),
  watch: {
    value(val) {
      this.isShow = val;
      if (val) this.load();
    },
    isShow(val) {
      if (val !== this.value) this.$emit('input', val);
    },
    administratorId() {
      if (this.isShow) this.load();
    },
  },
  computed: {
    ...mapGetters(['isAdministrator']),
    hasOpenEmployment() {
      return this.employmentRecords.some((record) => Number(record.is_current) === 1);
    },
    canRemoveWechat() {
      return this.isAdministrator
        && this.wechatStatus
        && Number(this.basic.is_del) > 0
        && !this.hasOpenEmployment
        && (this.wechatStatus.exists === true || this.wechatStatus.exists === null);
    },
    // 未绑定（exists=false 且无 userid），或绑定的 userid 已在通讯录里查不到时，允许补建
    canBindWechat() {
      return this.isAdministrator
        && !!this.wechatStatus
        && Number(this.basic.is_del) === 0
        && this.wechatStatus.exists === false;
    },
    // 成员已在通讯录里，但对方没收到（或误删了）邀请时，允许重发
    canResendInvite() {
      return this.isAdministrator
        && !!this.wechatStatus
        && Number(this.basic.is_del) === 0
        && this.wechatStatus.exists === true;
    },
    canRetryWechat() {
      return this.canBindWechat || this.canResendInvite;
    },
    retryTip() {
      if (this.canBindWechat) {
        return '仅超级管理员可操作；用于入职审核通过时企业微信接口调用失败（如服务器 IP 未加入可信IP）的事后补救，'
          + '按手机号创建成员并发送邀请，已在通讯录中的成员会被直接绑定，重复操作不会建出第二个成员。';
      }
      return '仅超级管理员可操作；成员已在企业微信通讯录中，若对方没收到加入邀请，可在此重新发送。';
    },
    wechatStatusText() {
      if (!this.wechatStatus) return '未查询';
      const status = this.wechatStatus.status;
      if (status === 'exists') return '存在于企业微信通讯录';
      if (status === 'not_exists') return '不在企业微信通讯录';
      if (status === 'unbound') return '未绑定企业微信号';
      if (status === 'unknown') return '查询失败';
      return this.wechatStatus.message || '-';
    },
    wechatTagType() {
      if (!this.wechatStatus) return 'info';
      if (this.wechatStatus.exists === true) return 'success';
      if (this.wechatStatus.exists === false) return 'info';
      return 'danger';
    },
  },
  methods: {
    async load() {
      if (!this.administratorId) return;
      this.isLoading = true;
      this.employmentRecords = [];
      this.wechatStatus = null;
      this.basic = {};
      const [recordResult, wechatResult] = await Promise.all([
        getEmploymentRecords(this.administratorId),
        getWeChatStatus(this.administratorId),
      ]);
      this.isLoading = false;

      if (!recordResult.isError) {
        this.employmentRecords = recordResult.data || [];
      }
      if (!wechatResult.isError) {
        this.wechatStatus = wechatResult.data || null;
        if (this.wechatStatus) {
          this.basic = { ...this.basic, ...this.wechatStatus };
        }
      }
    },
    async loadWeChatStatus() {
      if (!this.administratorId) return;
      this.wechatLoading = true;
      const { isError, data } = await getWeChatStatus(this.administratorId);
      this.wechatLoading = false;
      if (isError) return;
      this.wechatStatus = data || null;
      if (this.wechatStatus) {
        this.basic = { ...this.basic, ...this.wechatStatus };
      }
    },
    async handleRemoveWechat() {
      if (!this.wechatStatus || this.isRemoving) return;
      try {
        await this.$confirm(
          '确认手动将该员工移出企业微信通讯录吗？此操作不可逆，请确认该员工已离职。',
          '手动移出企业微信',
          { type: 'warning' },
        );
      } catch (e) {
        return;
      }

      this.isRemoving = true;
      const { isError } = await removeWeChat(this.administratorId);
      this.isRemoving = false;
      if (isError) return;

      this.$message.success('已移出企业微信通讯录');
      this.loadWeChatStatus();
    },
    async handleBindWechat() {
      if (this.isBinding) return;
      try {
        await this.$confirm(
          '确认按该员工的手机号在企业微信通讯录创建成员并发送加入邀请吗？已存在的成员会直接绑定，不会重复创建。',
          '补建企业微信成员',
          { type: 'warning' },
        );
      } catch (e) {
        return;
      }

      this.isBinding = true;
      const { isError, data } = await retryWeChatBind(this.administratorId);
      this.isBinding = false;
      if (isError) return;

      // message 非空是「成员已绑定、但邀请没发出去」的部分成功，不能当成完全成功提示
      if (data && data.message) {
        this.$message.warning(data.message);
      } else {
        this.$message.success('已补建企业微信成员并发送邀请');
      }
      this.loadWeChatStatus();
      // we_chat 已回写，通知列表页刷新企业微信号列
      this.$emit('updated');
    },
    async handleResendInvite() {
      if (this.isInviting) return;

      this.isInviting = true;
      const { isError } = await resendWeChatInvite(this.administratorId);
      this.isInviting = false;
      if (isError) return;

      this.$message.success('已重新发送企业微信加入邀请');
    },
    leaveTypeName(value) {
      const item = Object.values(leaveTypes).find((type) => Number(type.value) === Number(value)) || {};
      return item.name || '';
    },
    toTimeText(value) {
      return timeToYmdHi(value);
    },
    toDurationText(value) {
      return formatDuration(value);
    },
    toStoreNameText(value) {
      return formatStoreName(value);
    },
    close() {
      this.$emit('input', false);
    },
  },
};
</script>

<style scoped>
.personnel-overview {
  display: flex;
  flex-wrap: wrap;
  margin-bottom: 12px;
  padding: 12px;
  background: #fafafa;
  border-radius: 4px;
}
.personnel-overview-item {
  width: 50%;
  display: flex;
  align-items: center;
  padding: 4px 0;
}
.personnel-overview-item .label {
  width: 84px;
  flex-shrink: 0;
  color: #909399;
}
.wechat-card {
  margin-bottom: 16px;
}
.wechat-card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}
.wechat-id {
  margin-top: 6px;
  color: #606266;
  font-size: 12px;
}
.wechat-error {
  margin-top: 6px;
  color: #f56c6c;
  font-size: 12px;
}
.wechat-status-empty {
  color: #909399;
  font-size: 13px;
}
.wechat-remove {
  margin-top: 12px;
}
.wechat-retry {
  margin-top: 12px;
}
.wechat-remove-tip {
  margin-top: 6px;
  color: #909399;
  font-size: 12px;
}
.section-title {
  font-weight: 600;
  margin-bottom: 8px;
}
.employment-list {
  max-height: 360px;
  overflow-y: auto;
}
.employment-item {
  border: 1px solid #ebeef5;
  border-radius: 6px;
  margin-bottom: 12px;
  overflow: hidden;
}
.employment-item:last-child {
  margin-bottom: 0;
}
.employment-item-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 12px;
  background: #f5f7fa;
  border-bottom: 1px solid #ebeef5;
}
.employment-store {
  font-weight: 600;
  color: #303133;
}
.employment-item-body {
  padding: 6px 12px;
}
.employment-item-row {
  display: flex;
  align-items: flex-start;
  padding: 4px 0;
  line-height: 1.6;
}
.employment-item-label {
  width: 72px;
  flex-shrink: 0;
  color: #909399;
}
.employment-empty {
  color: #909399;
  padding: 16px 0;
  text-align: center;
}
.personnel-footer {
  margin-top: 16px;
  text-align: right;
}
</style>
